import { type TFunction } from "i18next";

import { Badge } from "@/components/ui/badge";
import { SortableColumnHeader } from "@/components/ui/sortable-column-header";
import { formatDateTimeCompact } from "@/utils/relative-time";

import { type FarmerDeleteReason, type FarmerDeleteReasonColumnDef } from "./types";

const HEADER_CLASS = "px-3 py-2 !whitespace-normal leading-tight";

export const FARMER_DELETE_REASON_SORT_OPTIONS = [
  { id: "text", translationKey: "configuration.common.name" },
  { id: "lastUpdated", translationKey: "configuration.list.column.lastUpdated" },
  { id: "created", translationKey: "configuration.list.column.created" },
] as const;

export const getFarmerDeleteReasonTranslation = (
  reason: FarmerDeleteReason,
  languageCode: string,
  t: TFunction
) => reason.translations?.[languageCode] || t("systemNotifications.noTranslatedText");

const AuditCell = ({
  dateTime,
  t,
  user,
}: {
  dateTime: string;
  t: TFunction;
  user: FarmerDeleteReason["creation_user"];
}) => {
  const fullName = user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() : "";

  return (
    <div>
      <div>{formatDateTimeCompact(dateTime)}</div>
      <div className="text-muted-foreground text-xs">
        {t("configuration.byFullName", { fullName: fullName || "System" })}
      </div>
    </div>
  );
};

interface CreateColumnsOptions {
  languageCode: string;
  languageLabel: string;
}

export const createColumns = (
  t: TFunction,
  { languageCode, languageLabel }: CreateColumnsOptions
): FarmerDeleteReasonColumnDef[] => [
  {
    id: "text",
    accessorFn: row => row.text,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.name")} />
    ),
    cell: ({ row }) => <span className="font-medium text-foreground">{row.original.text}</span>,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    meta: { cellClassName: "min-w-48", headerClassName: `min-w-48 ${HEADER_CLASS}` },
  },
  {
    id: "translation",
    header: () => t("commodities.list.column.translation", { language: languageLabel }),
    cell: ({ row }) => getFarmerDeleteReasonTranslation(row.original, languageCode, t),
    enableSorting: false,
    meta: { cellClassName: "min-w-40", headerClassName: `min-w-40 ${HEADER_CLASS}` },
  },
  {
    id: "lastUpdated",
    accessorFn: row => row.last_update_datetime,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.lastUpdated")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.last_update_datetime}
        t={t}
        user={row.original.last_update_user}
      />
    ),
    sortingFn: "alphanumeric",
    meta: { cellClassName: "min-w-40", headerClassName: `min-w-40 ${HEADER_CLASS}` },
  },
  {
    id: "created",
    accessorFn: row => row.creation_datetime,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.created")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.creation_datetime}
        t={t}
        user={row.original.creation_user}
      />
    ),
    sortingFn: "alphanumeric",
    meta: { cellClassName: "min-w-40", headerClassName: `min-w-40 ${HEADER_CLASS}` },
  },
  {
    id: "active",
    header: () => t("configuration.common.active"),
    cell: ({ row }) => (
      <Badge variant={row.original.active ? "success" : "secondary"}>
        {t(row.original.active ? "common.yes" : "common.no")}
      </Badge>
    ),
    enableSorting: false,
    meta: { headerClassName: HEADER_CLASS },
  },
];
